import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography, Paper, Toolbar, AppBar, CircularProgress, IconButton } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import SelectableImage from './SelectableImage';
import DeviceControls from './DeviceControls';
import DeviceSchedule from './DeviceSchedule';


function DeviceCard({ client, device, deviceID, deviceStatus, backgroundImage, offlineImage, focusDevice, focusedDevice, refreshStatus }) {
    const { t } = useTranslation();
    const [scale, setScale] = React.useState(0.5);
    const [paperElement, setPaperElement] = React.useState(null);
    const [deviceTick, setDeviceTick] = React.useState(0);
    const [tickResetKey, setTickResetKey] = React.useState(0);
    const [schedule, setSchedule] = React.useState(client.schedule ?? null);

    const resetTick = React.useCallback(() => {
        setDeviceTick(0);
        setTickResetKey(k => k + 1);
        refreshStatus?.();
    }, [refreshStatus]);

    React.useEffect(() => {
        setDeviceTick(0);
        const interval = setInterval(() => setDeviceTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, [tickResetKey]);

    const paperRef = React.useCallback(node => {
        setPaperElement(node);
    }, []);

    React.useEffect(() => {
        if (!device?.imagewidth || !paperElement) return;
        
        const updateScale = () => {
            const paperWidth = paperElement.offsetWidth - 32;
            const deviceWidth = parseInt(device.imagewidth);
            const newScale = Math.min(paperWidth / deviceWidth, 1);
            setScale(newScale);
        };
        
        const observer = new ResizeObserver(updateScale);
        observer.observe(paperElement);
        updateScale();
        
        return () => observer.disconnect();
    }, [device, paperElement]);


    if (!deviceStatus) return <CircularProgress />;
    if (!device) return <div>{t('deviceCard.deviceNotFound')}</div>;

    return (
        <Box>
            <Paper
                ref={paperRef}
                elevation={4}
                sx={{
                    p: 2,
                    //height: 950,
                    position: 'relative'
                }}
            >
                <AppBar position="static"
                    sx={{
                        mb: 2,
                        background: client.online ? '#019c00ff' : 'black'
                    }}>
                    <Toolbar variant="dense">
                        <Typography variant="h5" sx={{ textTransform: 'uppercase' }}>
                            {deviceID}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => focusDevice && focusDevice(deviceID)}
                            sx={{ ml: 'auto' }}
                        >
                            <MyLocationIcon fontSize="small" sx={{ color: focusedDevice === deviceID ? 'yellow' : 'white' }} />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Box sx={{
                    height: device?.imageheight ? device.imageheight * scale : undefined,
                    width: device?.imagewidth ? device.imagewidth * scale : undefined,
                    overflow: 'hidden',
                    flexShrink: 0,
                }}>
                <Box sx={{
                    height: device && device.imageheight ? device.imageheight : undefined,
                    width: device && device.imagewidth ? device.imagewidth : undefined,
                    overflow: 'hidden',
                    backgroundImage: client.online ? (backgroundImage ? `url(${backgroundImage})` : 'none') : (offlineImage ? `url(${offlineImage})` : 'foo'),
                    backgroundSize: 'contain',
                    backgroundPosition: 'top left',
                    backgroundRepeat: 'no-repeat',
                    alignItems: 'flex-start',
                    position: 'relative',
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                }}>
                    {client.online && deviceStatus.images && deviceStatus.images.map((image, idx) => {
                        if (!image) return null;
                        const board = device.components.find((comp => comp.position === idx));
                        if (!board) return null;
                        return (
                            <SelectableImage
                                key={idx}
                                src={image.name}
                                deviceID={deviceID}
                                deviceTypeID={device.dtid}
                                boardID={idx}
                                boardHeight={board.height}
                                height={board.screenheight}
                                width={board.screenwidth}
                                scale={board.screendimensionratio}
                                tick={deviceTick}
                                onImageChange={resetTick}
                                sx={{
                                    width: board.screenwidth,
                                    height: board.screenheight,
                                    display: 'inline-block',
                                    position: 'absolute',
                                    top: board.margintop,
                                    left: board.marginleft,
                                    marginLeft: 0,
                                    marginTop: 0,
                                }}
                            />
                        );
                    })}
                </Box>
                </Box>
                <DeviceControls deviceStatus={deviceStatus} deviceID={deviceID} online={client.online} />
                <DeviceSchedule deviceID={deviceID} device={device} schedule={schedule} onScheduleChange={setSchedule} />
            </Paper>
        </Box>
    );
}

export default DeviceCard;